import { useRef, useState } from 'react';

import type { PropsWithChildren } from 'react';
import type { Meta, StoryFn } from '@storybook/react';
import type { RteEditorProps } from './RteEditor';
import type { SearchResponse } from './Plugins/CourseCataloguePlugin/AddCatalogueLinkForm';

import { RteEditor } from './RteEditor';
import { MOCK_RTE_CONTENT } from './mockRteContent';
import { MOCK_SEARCH_COURSES, MOCK_SEARCH_PROGRAMMES } from './mockCatalogueSearchData';

import { Container } from 'storyComponents/Container';

const QUERY_SPEED = 1000; // ms
const CHARACTER_THRESHOLD = 3;

const codeString = `<RteEditor
          editorId="editor-id"
          initialValue=""
          currentLanguage="NL"
          label=""
          info=""
          placeholder=""
          notValidatedText=""
          onChange={handleChange}
          onCatalogueLinkSearch={handleCatalogueLinkSearch}
          catalogueSearchResponse={{ state: 'idle', results: [] }}
        />`;

// eslint-disable-next-line import/no-default-export
export default {
  title: 'Organisms/RteEditor',
  component: RteEditor,
  parameters: {
    inspectComponent: RteEditor,
    codeString: codeString,
  },
} as Meta<PropsWithChildren<RteEditorProps>>;

const Template: StoryFn<PropsWithChildren<RteEditorProps>> = (args) => {
  const [result, setResult] = useState<string>(args.initialValue || '');
  const [catalogueSearchResponse, setCatalogueSearchResponse] = useState<SearchResponse>({
    state: 'idle',
    results: [],
  });
  const networkTimer = useRef<NodeJS.Timeout | null>(null);

  const handleSearch = (query: string, linkType: 'course' | 'programme') => {
    if (query.length < CHARACTER_THRESHOLD) {
      clearTimeout(networkTimer.current!);
      setCatalogueSearchResponse({ state: 'idle', results: [] });
      return;
    }

    if (networkTimer.current !== null) {
      clearTimeout(networkTimer.current);
    }

    const lowerCaseQuery = query.toLowerCase();
    if (query.length >= CHARACTER_THRESHOLD) {
      setCatalogueSearchResponse({ state: 'pending', results: [] });
      const mockResults =
        linkType === 'course'
          ? MOCK_SEARCH_COURSES.filter(
              (item) =>
                item.name.NL.toLowerCase().includes(lowerCaseQuery) || item.code.toLowerCase().includes(lowerCaseQuery)
            )
          : MOCK_SEARCH_PROGRAMMES.filter(
              (item) =>
                item.name.NL.toLowerCase().includes(lowerCaseQuery) || item.code.toLowerCase().includes(lowerCaseQuery)
            );

      networkTimer.current = setTimeout(
        () =>
          setCatalogueSearchResponse({
            state: 'ready',
            results: mockResults,
          }),
        QUERY_SPEED
      );
    }
  };

  return (
    <>
      <Container size="large-full-height">
        <RteEditor
          {...args}
          onChange={(evt) => setResult(evt)}
          onCatalogueLinkSearch={handleSearch}
          catalogueSearchResponse={catalogueSearchResponse}
        />
      </Container>
      <br />
      <br />

      <p style={{ margin: '0', marginTop: '2rem' }}>HTML Output:</p>
      <div
        style={{
          marginTop: '0rem',
          padding: '1rem',
          backgroundColor: '#F9F9F9',
          borderRadius: '0.25rem',
          color: '#666',
        }}
      >
        {result}
      </div>
    </>
  );
};

export const RteEditorExample = Template.bind({});

RteEditorExample.args = {
  editorId: 'test-editor',
  placeholder: 'Voeg hier tekst in...',
  currentLanguage: 'NL',
  label: 'Studiemateriaal',
  info: "Studiemateriaal is essentieel voor succesvol leren. Het omvat boeken, artikelen, video's en oefeningen die studenten helpen hun kennis te vergroten, vaardigheden te ontwikkelen en hun studieprestaties te verbeteren.",
  required: true,
  initialValue: MOCK_RTE_CONTENT,
};

export const MultipleRteEditors = () => {
  const [result01, setResult01] = useState<string>(
    'Students will:<br>• demonstrate digital literacy in an academic context<br>• learn to read and engage with texts from a range of disciplinary perspectives<br>• develop writing and argumentation skills appropriate to different disciplines<br>• learn to recognize and apply different research methodologies<br>• learn how to recognize and practice different genres and modes of academic discourse<br>• develop vocabulary appropriate to an academic environment<br>• improve analytic and critical thinking skills<br>• work independently and as part of a collaborative group<br>• learn to review and critique their own work and that of their peers <br>• improve listening comprehension and develop presentation skills<br>• participate in discussions, workshops and debates'
  );
  const [result02, setResult02] = useState<string>(
    `<p>Editor 2</p>
    <ul>
      <li>nesting</li>
      <li>
        <ol>
          <li>nested 1</li>
          <li>nested 2</li>
        </ol>
      </li>
      <li>nesting 2</li>
      <li>
        <ul>
          <li>deep nested 1</li>
            <li>deep nested 2</li>
            <li>
              <ol>
                <li>deeper nested 1</li>
                <li>
                <ul>
                <li>deepest nested 1</li>
                <li>deepest nested 2</li>
                </ul>
                </li>
                <li>deeper nested 2</li>
                <li>
                  <ol>
                    <li>deepest nested 1</li>
                    <li>deepest nested 2</li>
                  </ol>
                </li>
              </ol>
            </li>
          </ul>
        </li>
      <li>nesting end</li>
    </ul>`
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '1rem',
        width: '100%',
        padding: '1rem',
        backgroundColor: '#FFF',
      }}
    >
      <div style={{ flex: '1' }}>
        <RteEditor
          initialValue={result01}
          editorId="editor-one"
          label="Editor 1"
          info="Uitleg over editor 1"
          placeholder="Voeg hier tekst in..."
          notValidatedText=""
          onChange={(evt) => setResult01(evt)}
        />
        <br />
        <p style={{ margin: '0', marginTop: '2rem' }}>HTML Output:</p>
        <div
          style={{
            marginTop: '0rem',
            padding: '1rem',
            backgroundColor: '#F9F9F9',
            borderRadius: '0.25rem',
            color: '#666',
          }}
        >
          {result01}
        </div>
      </div>
      <div style={{ flex: '1' }}>
        <RteEditor
          initialValue={result02}
          editorId="editor-two"
          label="Editor 2"
          info="Uitleg over editor 2 wat langer om te kijken of dat ook goed gaat, want je weet maar nooit."
          placeholder="Voeg hier tekst in..."
          notValidatedText=""
          onChange={(evt) => setResult02(evt)}
        />
        <br />
        <p style={{ margin: '0', marginTop: '2rem' }}>HTML Output:</p>
        <div
          style={{
            marginTop: '0rem',
            padding: '1rem',
            backgroundColor: '#F9F9F9',
            borderRadius: '0.25rem',
            color: '#666',
          }}
        >
          {result02}
        </div>
      </div>
    </div>
  );
};
